import * as XLSX from 'xlsx';
import {
  CORE_IMPORT_HEADERS,
  IMPORT_ACCEPTED_EXTENSIONS,
  IMPORT_MAX_FILE_BYTES,
} from './constants';

const DEFAULT_SHEET_NAME = 'Reminder Import';

function sanitizeSheetName(name) {
  const cleaned = String(name || DEFAULT_SHEET_NAME)
    .replace(/[:\\/?*[\]]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return (cleaned || DEFAULT_SHEET_NAME).slice(0, 31);
}

function sanitizeFileName(name) {
  const cleaned = String(name || 'template')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .trim();
  return cleaned || 'template';
}

function columnHeader(col) {
  return String(col?.header || col?.key || '');
}

function isEventDateColumn(col) {
  return columnHeader(col).toLowerCase().replace(/\s+/g, '_') === 'event_date';
}

const EVENT_DATE_COLUMN = {
  header: 'event_date',
  key: 'event_date',
  required: true,
  description: 'YYYY-MM-DD or DD/MM/YYYY',
  example: '2026-10-31',
};

/** Columns the downloaded file and the columns table both use. */
export function schemaForSendingMode(
  schema,
  sendingMode,
  { requireEventDate = false } = {},
) {
  if (!schema) return schema;
  const columns = Array.isArray(schema.columns) ? schema.columns : [];
  const wantEventDate = sendingMode === 'yearly_date' || requireEventDate;
  if (!wantEventDate) {
    return {
      ...schema,
      columns: columns.filter((col) => !isEventDateColumn(col)),
    };
  }
  if (!columns.some(isEventDateColumn)) {
    if (sendingMode !== 'yearly_date') return { ...schema, columns };
    const copy = [...columns];
    const nameIdx = copy.findIndex(
      (col) => columnHeader(col).toLowerCase() === 'name',
    );
    copy.splice(nameIdx >= 0 ? nameIdx + 1 : copy.length, 0, {
      ...EVENT_DATE_COLUMN,
    });
    return { ...schema, columns: copy };
  }
  return {
    ...schema,
    columns: columns.map((col) =>
      isEventDateColumn(col)
        ? {
            ...col,
            required: true,
            description: col.description || EVENT_DATE_COLUMN.description,
          }
        : col,
    ),
  };
}

function sampleCell(col, sampleRow, index) {
  if (Array.isArray(sampleRow)) {
    return sampleRow[index] == null ? '' : sampleRow[index];
  }
  if (sampleRow && typeof sampleRow === 'object') {
    if (col.key != null && sampleRow[col.key] != null)
      return sampleRow[col.key];
    if (col.header != null && sampleRow[col.header] != null) {
      return sampleRow[col.header];
    }
  }
  if (col?.example != null) return col.example;
  return '';
}

export function schemaHasOnlyCoreColumns(columns) {
  if (!Array.isArray(columns) || columns.length === 0) return false;
  return columns.every((col) =>
    CORE_IMPORT_HEADERS.includes(columnHeader(col).toLowerCase()),
  );
}

export function getSchemaVariableHeaders(columns) {
  return (Array.isArray(columns) ? columns : [])
    .map((col) => columnHeader(col))
    .filter(
      (header) => header && !CORE_IMPORT_HEADERS.includes(header.toLowerCase()),
    );
}

export function getImportTemplateFileName(schema) {
  return `reminder_import_${sanitizeFileName(schema?.template_name)}.xlsx`;
}

/**
 * Build an XLSX workbook from the import schema API.
 * Headers come from columns[].header in order. Never invent column names.
 */
export function buildReminderImportWorkbook(schema) {
  const columns = Array.isArray(schema?.columns) ? schema.columns : [];
  const headers = columns.map((col) => col.header);
  const sampleRow = Array.isArray(schema?.sample_rows)
    ? schema.sample_rows[0]
    : null;

  const dataRow = columns.map((col, index) =>
    sampleCell(col, sampleRow, index),
  );

  const ws = XLSX.utils.aoa_to_sheet([headers, dataRow]);
  ws['!views'] = [{ state: 'frozen', ySplit: 1, topLeftCell: 'A2' }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(schema?.sheet_name));
  return wb;
}

export function downloadReminderImportTemplate(schema) {
  const columns = schema?.columns;
  if (!Array.isArray(columns) || columns.length === 0) {
    throw new Error('Import schema has no columns.');
  }

  const wb = buildReminderImportWorkbook(schema);
  XLSX.writeFile(wb, getImportTemplateFileName(schema));
}

export function triggerBlobDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function validateReminderImportFile(file) {
  if (!file) return 'Please select a file.';
  if (file.size === 0) return 'File is empty.';
  if (file.size > IMPORT_MAX_FILE_BYTES) {
    return 'File is too large. Max 10 MB.';
  }
  const name = String(file.name || '').toLowerCase();
  const allowed = IMPORT_ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
  if (!allowed) return 'Use an .xlsx or .csv file.';
  return null;
}
